import type { CVListItemDto } from '@cibli/shared';

import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { downloadCV } from '@/services/interview';

export type DownloadableCV = Pick<CVListItemDto, 'id' | 'firstName' | 'lastName'>;

interface UseDownloadCVReturn {
  downloadCVFromList: (cv: DownloadableCV) => Promise<void>;
  isDownloading: boolean;
  downloadingId: string | null;
  error: string | null;
}

export function useDownloadCV(): UseDownloadCVReturn {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const downloadCVFromList = async (cv: DownloadableCV) => {
    let toastId: string | number | undefined;
    try {
      setIsDownloading(true);
      setDownloadingId(cv.id);
      setError(null);
      toastId = toast.loading(t('dashboard.cvList.download.downloading'));

      const pdfBlob = await downloadCV(cv.id);

      const url = URL.createObjectURL(pdfBlob);

      const firstName = cv.firstName?.trim();
      const lastName = cv.lastName?.trim();
      const filename = [firstName, lastName].filter(Boolean).join('-') || cv.id;

      const a = document.createElement('a');
      a.href = url;
      a.download = `CV-${filename}.pdf`;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      toast.success(t('dashboard.cvList.download.success'), { id: toastId });
    } catch (err) {
      console.error('Error downloading CV', err);
      Sentry.captureException(err, {
        tags: { feature: 'cv-download' },
        extra: { cvId: cv.id },
      });
      const message = t('dashboard.cvList.download.error');
      setError(message);
      toast.error(message, toastId ? { id: toastId } : undefined);
    } finally {
      setIsDownloading(false);
      setDownloadingId(null);
    }
  };

  return {
    downloadCVFromList,
    isDownloading,
    downloadingId,
    error,
  };
}
